import { useState, useEffect, useCallback } from 'react';
import googleSheets from '../services/googleSheets';
import { OnboardingData, CustomerProgress } from '../types';
import { voiceService } from '../utils/voiceService';

interface User {
  email: string;
  name: string;
}

export const useOnboardingData = (customerId: string, user?: User | null) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [onboardingData, setOnboardingData] = useState<OnboardingData | null>(null);
  const [updating, setUpdating] = useState(false);
  const [authError, setAuthError] = useState(false);

  const loadOnboardingData = useCallback(async (forceRefresh: boolean = false) => {
    try {
      setLoading(true);
      setError(null);
      setAuthError(false);
      const data = await googleSheets.getOnboardingData(customerId, forceRefresh);
      if (!data) {
        setError('Customer not found');
        return;
      }
      setOnboardingData(data);
    } catch (err: any) {
      if (err?.message === 'AUTHENTICATION_EXPIRED') {
        setAuthError(true);
        setError('Please sign in again to continue');
      } else {
        setError('Failed to load onboarding data');
      }
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    loadOnboardingData();
  }, [loadOnboardingData]);

  const handleTaskStatusChange = useCallback(async (
    taskId: string, 
    status: CustomerProgress['status']
  ) => {
    if (!onboardingData || updating) return;

    try {
      setUpdating(true);
      
      const userInfo = user ? { email: user.email, name: user.name } : undefined;
      
      // Optimistic UI update - update local state immediately with validation
      setOnboardingData(prev => {
        if (!prev) return prev;
        
        // Validate task exists in current data
        const taskExists = prev.tasks.some(t => t.id === taskId);
        if (!taskExists) {
          console.warn(`Task ${taskId} not found in current tasks`);
          return prev;
        }
        
        const hasExistingProgress = prev.progress.some(p => p.taskId === taskId);
        let updatedProgress: CustomerProgress[];
        
        if (hasExistingProgress) {
          // Update existing progress entry
          updatedProgress = prev.progress.map(p => 
            p.taskId === taskId 
              ? { 
                  ...p, 
                  status, 
                  completedDate: status === 'completed' ? new Date().toISOString() : p.completedDate,
                  updatedByEmail: userInfo?.email,
                  updatedByName: userInfo?.name
                }
              : p
          );
        } else {
          // Add new progress entry only if it doesn't exist
          updatedProgress = [
            ...prev.progress,
            {
              customerId,
              taskId,
              status,
              completedDate: status === 'completed' ? new Date().toISOString() : undefined,
              updatedByEmail: userInfo?.email,
              updatedByName: userInfo?.name
            }
          ];
        }
        
        return {
          ...prev,
          progress: updatedProgress
        };
      });
      
      // Update server data
      await googleSheets.updateTaskProgress(customerId, taskId, status, userInfo);
      
      // Strategic refresh: only update calculated metrics without full data reload
      // This ensures health score and customer status are accurate
      setTimeout(async () => {
        try {
          const refreshedData = await googleSheets.getOnboardingData(customerId, true);
          if (refreshedData) {
            setOnboardingData(prev => prev ? {
              ...prev,
              // Only update calculated/server-side data, keep optimistic UI updates
              customer: refreshedData.customer, // Status might have changed
              healthScore: refreshedData.healthScore // Recalculated on server
            } : refreshedData);
          }
        } catch (refreshError) {
          // Silently fail - optimistic updates are still visible
          console.warn('Failed to refresh calculated metrics:', refreshError);
        }
      }, 500); // Small delay to allow server calculations to complete
      
      if (status === 'completed') {
        showCelebration();
        
        if (userInfo?.name && onboardingData) {
          const task = onboardingData.tasks.find(t => t.id === taskId);
          if (task) {
            try {
              await voiceService.speakCongratulations(userInfo.name, task.title);
            } catch (voiceError) {
              // Voice congratulations failed - continue silently
            }
          }
        }
      }
    } catch (err: any) {
      if (err?.message === 'AUTHENTICATION_EXPIRED') {
        setAuthError(true);
        setError('Please sign in again to continue');
      } else {
        setError('Failed to update task. Please try again.');
      }
      // Revert optimistic update on error
      await loadOnboardingData(true);
    } finally {
      setUpdating(false);
    }
  }, [customerId, onboardingData, updating, user, loadOnboardingData]);

  const showCelebration = () => {
    const celebration = document.createElement('div');
    celebration.className = 'celebration';
    celebration.textContent = '🎉 Great job!';
    document.body.appendChild(celebration);
    
    setTimeout(() => {
      document.body.removeChild(celebration);
    }, 2000);
  };

  const retryAuth = useCallback(async () => {
    setAuthError(false);
    setError(null);
    await loadOnboardingData(true);
  }, [loadOnboardingData]);

  return {
    loading,
    error,
    onboardingData,
    updating,
    authError,
    loadOnboardingData,
    handleTaskStatusChange,
    retryAuth,
  };
};